// src/composables/useGlobalBetslipWatcher.js
import { onMounted, onBeforeUnmount, watch } from 'vue'
import { useBetSlipStore } from '@/stores/betSlipStore'
import { isFixtureExpired } from '@/utils/expiry'

/**
 * Global betslip watcher that monitors odds updates and expired bets
 * across all routes via WebSocket connections
 */
export function useGlobalBetslipWatcher() {
  const betSlipStore = useBetSlipStore()

  let liveWebSocket = null
  let prematchWebSocket = null
  let expiryCheckInterval = null

  /**
   * Connect to live games WebSocket for odds updates
   */
  const connectLiveWebSocket = () => {
    // Only connect if there are live bets in the betslip
    const liveBets = betSlipStore.bets.filter((bet) => bet.live === 'l' || bet.is_live === true)
    if (liveBets.length === 0) {
      if (liveWebSocket?.readyState === WebSocket.OPEN) {
        liveWebSocket.close()
      }
      return
    }

    // Close existing connection
    if (liveWebSocket?.readyState === WebSocket.OPEN) {
      liveWebSocket.close()
    }

    // Get unique sport IDs from live bets
    const sportIds = [...new Set(liveBets.map((bet) => bet.sport_id))]

    // Connect to live WebSocket for the first sport (or extend for multiple sports)
    const sportId = sportIds[0] || 10
    const wsUrl = `${import.meta.env.VITE_WSS}mb/fixtures/live/websock/test?sport_id=${sportId}&market_id=2`

    console.log('🔴 Connecting to live WebSocket for betslip updates:', wsUrl)
    liveWebSocket = new WebSocket(wsUrl)

    liveWebSocket.onopen = () => {
      console.log('✅ Live WebSocket connected for betslip monitoring')
    }

    liveWebSocket.onmessage = ({ data }) => {
      if (!data) return

      try {
        const response = JSON.parse(data)
        if (response.data && Array.isArray(response.data)) {
          // Process each game for odds updates
          response.data.forEach((game) => {
            processGameUpdate(game, true)
          })
          // Update stale status for live bets after processing all games
          updateStaleBets(response.data)
        }
      } catch (e) {
        console.error('Error parsing live WebSocket message:', e)
      }
    }

    liveWebSocket.onerror = (err) => {
      console.error('Live WebSocket error:', err)
    }

    liveWebSocket.onclose = () => {
      console.log('Live WebSocket closed')
    }
  }

  /**
   * Process game update from WebSocket
   * @param {Object} game - Game data from WebSocket
   * @param {Boolean} isLive - Whether this is a live game
   */
  // eslint-disable-next-line no-unused-vars
  const processGameUpdate = (game, isLive = false) => {
    const matchId = game.match_id || game.game_id

    // Check if this match has a bet in the betslip
    const betInSlip = betSlipStore.bets.find((bet) => bet.match_id === matchId)
    if (!betInSlip) return

    // Find the market and outcome for this bet
    const market = game.markets?.find((m) => m.market_id === betInSlip.market_id)
    if (!market) return

    const outcome = market.oddsArray?.find((o) => o.outcome_id === betInSlip.outcome_id)
    if (!outcome) return

    // Check if odds have changed
    const currentOdds = parseFloat(betInSlip.odds)
    const newOdds = parseFloat(outcome.odds)

    if (currentOdds !== newOdds) {
      if (betSlipStore.acceptOddsChanges) {
        // Auto-update the odds
        betSlipStore.updateBetOdds(
          matchId,
          newOdds,
          outcome.outcome_name,
          outcome.alias,
          market.status_name,
        )
        console.log(`✅ Auto-updated odds for match ${matchId}: ${currentOdds} → ${newOdds}`)
      } else {
        // Mark as changed without updating
        const bet = betSlipStore.bets.find((b) => b.match_id === matchId)
        if (bet && !bet.oddsChanged) {
          bet.oddsChanged = true
          bet.newOdds = newOdds
          betSlipStore.saveToLocalStorage()
          console.log(
            `⚠️ Odds changed for match ${matchId}: ${currentOdds} → ${newOdds} (not auto-updated)`,
          )
        }
      }
    }

    // Update market status if changed
    const currentStatus = betInSlip.status_name?.toLowerCase().trim()
    const newStatus = market.status_name?.toLowerCase().trim()

    if (currentStatus !== newStatus) {
      betSlipStore.updateBetOdds(
        matchId,
        betInSlip.odds,
        betInSlip.outcome_name,
        betInSlip.alias,
        market.status_name,
      )
      console.log(`📊 Updated market status for match ${matchId}: ${currentStatus} → ${newStatus}`)
    }
  }

  /**
   * Update stale status for live bets based on active games
   * Similar to existing_project implementation
   * @param {Array} liveGames - Array of active live games from WebSocket
   */
  const updateStaleBets = (liveGames) => {
    // Create set of active match IDs from live games
    const activeMatches = new Set(liveGames.map((g) => g.match_id || g.game_id))

    // Update stale status for all live bets
    betSlipStore.bets.forEach((bet) => {
      if (bet.live === 'l' || bet.is_live === true) {
        const wasStale = bet.stale
        bet.stale = !activeMatches.has(bet.match_id)

        if (wasStale !== bet.stale) {
          console.log(
            `🔄 Updated stale status for match ${bet.match_id}: ${wasStale} → ${bet.stale}`,
          )
        }
      }
    })

    // Save changes to localStorage
    betSlipStore.saveToLocalStorage()
  }

  /**
   * Check for expired bets periodically
   */
  const checkExpiredBets = () => {
    if (betSlipStore.bets.length === 0) return

    let hasExpired = false
    const expiredBets = []

    betSlipStore.bets.forEach((bet) => {
      if (isFixtureExpired(bet)) {
        hasExpired = true
        expiredBets.push(bet)
        console.log(`⏰ Detected expired bet for match ${bet.match_id}`)
      }
    })

    // If expired bets found, just log them (don't auto-remove)
    // Let the user see the expired state and manually remove them
    if (hasExpired) {
      console.log(
        `⚠️ Betslip contains ${expiredBets.length} expired bets - showing as expired in UI`,
      )
      // The UI will show "Remove expired Markets" buttons for manual removal
    }
  }

  /**
   * Watch for changes in betslip to reconnect WebSocket if needed
   */
  const watchBetslipChanges = () => {
    watch(
      () => betSlipStore.bets.length,
      (newLength, oldLength) => {
        console.log(`📊 Betslip changed: ${oldLength} → ${newLength} bets`)

        // Reconnect WebSocket if live bets exist
        const liveBets = betSlipStore.bets.filter((bet) => bet.live === 'l' || bet.is_live === true)
        if (liveBets.length > 0) {
          connectLiveWebSocket()
        } else if (liveWebSocket?.readyState === WebSocket.OPEN) {
          liveWebSocket.close()
        }
      },
      { immediate: true },
    )

    // Watch for accept odds changes toggle
    watch(
      () => betSlipStore.acceptOddsChanges,
      (acceptChanges) => {
        if (acceptChanges) {
          // Apply all pending odds changes
          betSlipStore.bets.forEach((bet) => {
            if (bet.oddsChanged && bet.newOdds) {
              bet.odds = bet.newOdds
              bet.oddsChanged = false
              delete bet.newOdds
              console.log(`✅ Applied pending odds change for match ${bet.match_id}`)
            }
          })
          betSlipStore.saveToLocalStorage()
        }
      },
    )
  }

  /**
   * Monitor specific live match for odds updates
   * @param {Number} matchId - Match ID to monitor
   */
  // eslint-disable-next-line no-unused-vars
  const monitorLiveMatch = (matchId) => {
    const bet = betSlipStore.bets.find((b) => b.match_id === matchId)
    if (!bet || bet.live !== 'l') return

    const wsUrl = `${import.meta.env.VITE_WSS}mb/fixtures/live/match_id/${matchId}`
    const matchWs = new WebSocket(wsUrl)

    matchWs.onopen = () => {
      console.log(`🔴 Monitoring live match ${matchId} for betslip updates`)
    }

    matchWs.onmessage = ({ data }) => {
      if (!data) return
      try {
        const response = JSON.parse(data)
        if (response.code === 200 && response.status === 'success' && response.data) {
          processGameUpdate(response.data, true)
        }
      } catch (e) {
        console.error('Error parsing match WebSocket message:', e)
      }
    }

    matchWs.onerror = (err) => {
      console.error(`WebSocket error for match ${matchId}:`, err)
    }

    matchWs.onclose = () => {
      console.log(`WebSocket closed for match ${matchId}`)
    }

    return matchWs
  }

  /**
   * Initialize the global betslip watcher
   */
  const initialize = () => {
    // Connect to WebSocket if there are live bets
    connectLiveWebSocket()

    // Set up periodic expiry check (every 30 seconds)
    expiryCheckInterval = setInterval(checkExpiredBets, 30000)

    // Watch for betslip changes
    watchBetslipChanges()

    // Initial expiry check
    checkExpiredBets()

    // Log betslip state
    const liveBets = betSlipStore.bets.filter((bet) => bet.live === 'l' || bet.is_live === true)
    const prematchBets = betSlipStore.bets.filter((bet) => bet.live !== 'l' && !bet.is_live)
  }

  /**
   * Cleanup function
   */
  const cleanup = () => {
    console.log('🛑 Cleaning up global betslip watcher')

    // Close WebSocket connections
    if (liveWebSocket?.readyState === WebSocket.OPEN) {
      liveWebSocket.close()
    }
    if (prematchWebSocket?.readyState === WebSocket.OPEN) {
      prematchWebSocket.close()
    }

    // Clear interval
    if (expiryCheckInterval) {
      clearInterval(expiryCheckInterval)
    }
  }

  // Lifecycle hooks
  onMounted(() => {
    initialize()
  })

  onBeforeUnmount(() => {
    cleanup()
  })

  return {
    initialize,
    cleanup,
    connectLiveWebSocket,
    checkExpiredBets,
  }
}
